// @avantia common-code
import { ensureDefined, getFromWindowRef, SMap } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import {
  createControlId,
  destructureControlId,
  findQuestionId,
  getIdentifierConfigDefaultValues,
  HpAnswerMap,
  HpAnswerValueTypes,
  HpCreateControlIdProps,
  HpQuestion,
  HpQuestionLookupItem,
  HpQuestionLookupItemMap,
  HpSection,
  HpTokensAndExpressionsThisReference,
  identifierConfigChanged
} from '@avantia/questionset-model';
import { HpQuestionType } from '@avantia/questionset-script-parser/types';
import jQuery from 'jquery';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logInfo } from '../clientLogging';
import { getCustomFunction } from '../customFunctionRegistry';
import {
  HpClientAnswer,
  HpClientPrerequisiteThisReference,
  HpClientQuestion,
  HpClientSection,
  HpClientThisRefGetItemFunctionObjectProps,
  HpClientThisRefGetItemFunctionProps,
  HpQuestionBasic,
  HpQuestionDisplayState,
  HpQuestionNotFoundAction,
  HpSectionBasic
} from '../types';
import { getAnswersFromAnswerMap } from './clientAndServerUtilities';
import { getAnswerValue } from './utilities';

export interface HpClientPrerequisiteThisReferenceClassProps<QuestionT, SectionT> {
  answers: SMap<HpClientAnswer>;
  commonLookups: SMap<HpQuestionLookupItemMap>;
  debugFlag: HpClientDebugFlags;
  displayState: HpQuestionDisplayState;
  groupNumber: number | undefined;
  indexes: number[] | undefined;
  options?: HpClientPrerequisiteThisReferenceClassOptions;
  productId: string;
  questions: SMap<QuestionT>;
  sections: SMap<SectionT>;
  sourceQuestionId: string;
  sourceQuestionType: HpQuestionType;
}

export interface HpClientPrerequisiteThisReferenceClassOptions {
  questionIdNotFoundAction: HpQuestionNotFoundAction;
}

interface HpThisSelf<QuestionT, SectionT> extends HpClientPrerequisiteThisReferenceClassProps<QuestionT, SectionT> {
  options: HpClientPrerequisiteThisReferenceClassOptions;
}

const hasWindowRef = !!getFromWindowRef([]);
let { maxGroupNesting } = getIdentifierConfigDefaultValues(); // just a temp default, it will be set correctly below.

identifierConfigChanged(({ maxGroupNesting: mgn }) => {
  maxGroupNesting = mgn;
});

export class HpClientPrerequisiteThisReferenceClass<
    QuestionT extends HpClientQuestion | HpQuestion,
    SectionT extends HpClientSection | HpSection
  >
  extends HpTokensAndExpressionsThisReference
  implements HpClientPrerequisiteThisReference<QuestionT>
{
  // This makes the property nominally more difficult for scripts to access values directly, rather than via functions
  private readonly _self: HpThisSelf<QuestionT, SectionT>;

  constructor({
    answers,
    commonLookups,
    debugFlag,
    displayState,
    groupNumber,
    indexes,
    options,
    productId,
    questions,
    sourceQuestionId,
    sourceQuestionType
  }: HpClientPrerequisiteThisReferenceClassProps<QuestionT, SectionT>) {
    super();
    this._self = {
      answers,
      commonLookups,
      debugFlag,
      displayState,
      groupNumber,
      indexes,
      options: options || { questionIdNotFoundAction: 'fail' }, // with sourceQuestionId set to '' for sections, we *hopefully* don't need the 'log' level any longer.
      productId,
      questions,
      sections: displayState.sections as any,
      sourceQuestionId,
      sourceQuestionType
    };
  }

  modifyState(questions: SMap<QuestionT>, answers: SMap<HpClientAnswer>, groupNumber: number | undefined): void {
    const self = this._self;
    self.questions = questions;
    self.answers = answers;
    self.groupNumber = groupNumber;
  }

  getQuestion(props: HpClientThisRefGetItemFunctionProps): QuestionT | undefined {
    const { groupNumber, questions, sections, options, indexes, sourceQuestionId, sourceQuestionType } = this._self;
    return getQuestionFunc<QuestionT, SectionT>({
      groupNumber,
      indexes,
      options,
      props,
      questions,
      sections,
      sourceQuestionId,
      sourceQuestionType
    });
  }

  getAnswer(props: HpClientThisRefGetItemFunctionProps): HpClientAnswer | undefined {
    const { groupNumber, questions, sections, answers, options, indexes, sourceQuestionId, sourceQuestionType } =
      this._self;
    return getAnswerFunc<QuestionT, SectionT>({
      answers,
      groupNumber,
      indexes,
      options,
      props,
      questions,
      sections,
      sourceQuestionId,
      sourceQuestionType
    });
  }

  /**
   * Gets the full answer map.
   * @returns The answer map
   */
  getAnswerMap(): HpAnswerMap {
    return this._self.answers;
  }

  /**
   * Gets an answer, first validating against a question.
   * @param props Either a questionId string or object including questionId
   * @returns An answer
   */
  getAnswerValue(props: HpClientThisRefGetItemFunctionProps): HpAnswerValueTypes {
    const { groupNumber, indexes, questions, sections, answers, options, sourceQuestionId, sourceQuestionType } =
      this._self;

    return getAnswerValueFunc<QuestionT, SectionT>({
      answers,
      groupNumber,
      indexes,
      options,
      props,
      questions,
      sections,
      sourceQuestionId,
      sourceQuestionType
    });
  }

  /**
   * WARNING: Use this with caution: ensure that it is called from within a reducer!
   *
   * Sets an answer, if it exists.
   * @param props Either a questionId string or object including questionId
   * @param value The value to set.
   * @returns true if the answer exists and was set.
   */
  setAnswerValue(props: HpClientThisRefGetItemFunctionProps, value: HpAnswerValueTypes): boolean {
    const answer = this.getAnswer(props);
    if (answer) {
      answer.customer = value;
    }

    return !!answer;
  }

  /**
   * Gets an answer value by name, but does not validate against a question.
   * Usually, 'getAnswerValue' will be the best function to call.
   * @param questionName The name of a question
   * @returns Either the answer or undefined
   */
  getAnswerValueByName(questionName: string): HpAnswerValueTypes | undefined {
    const { answers, productId } = this._self;
    const result = getAnswersFromAnswerMap<any>({
      productId,
      answers,
      keys: [questionName],
      options: { returnAnyAnswerType: true }
    });
    const res = result[questionName];
    return res;
  }

  isElementVisible(questionId: string, groupNum: number | undefined): boolean {
    const { displayState } = this._self;
    return isElementVisible(questionId, groupNum, displayState);
  }

  getProductId(): string {
    return this._self.productId;
  }

  getIdFromName(name: string, groupNumber: number | undefined): string {
    return getIdFromName(name, groupNumber);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  getCustomFunction(name: string): Function {
    return getCustomFunction('*', name);
  }

  getCommonLookupMap(name: string): SMap<HpQuestionLookupItem> {
    const { commonLookups } = this._self;
    const map = commonLookups[name] as SMap<HpQuestionLookupItem>;
    if (!map) {
      throw new Error(`There is no common lookup map with name "${name}".`);
    }

    return map;
  }
}

interface GetQuestionFuncProps<QuestionT extends HpQuestionBasic, SectionT extends HpSectionBasic> {
  props: HpClientThisRefGetItemFunctionProps; // the props that are provided by the question-set function (which may be just a questionId string).
  groupNumber: number | undefined; // the group number of the group being interacted with.
  indexes: number[] | undefined; // the parent indexes of the group being interacted with.
  options: HpClientPrerequisiteThisReferenceClassOptions;
  questions: SMap<QuestionT>;
  sections: SMap<SectionT>;
  sourceQuestionId: string;
  sourceQuestionType: HpQuestionType;
}

function getQuestionFunc<QuestionT extends HpQuestionBasic, SectionT extends HpSectionBasic>({
  groupNumber,
  indexes,
  options,
  props,
  questions,
  sections,
  sourceQuestionId,
  sourceQuestionType
}: GetQuestionFuncProps<QuestionT, SectionT>): QuestionT | undefined {
  let ctrlProps: HpCreateControlIdProps;
  let questionType: HpQuestionType;
  if (typeof props === 'string') {
    ctrlProps = destructureControlId(props as string, { requirePrefix: false });
  } else {
    ({ questionType } = props as HpClientThisRefGetItemFunctionObjectProps);
    ctrlProps = props;
  }

  const { groupNumber: groupNumberOverride, questionId } = ctrlProps;
  let question: QuestionT | undefined;
  if (questionType !== 'section') {
    question = findBasicQuestion(questionId, questions, options.questionIdNotFoundAction);
  }

  if (question && sourceQuestionType !== 'section') {
    // the parent group is attempting to modify a child group, or vice versa
    ({ indexes, groupNumber } = modifyIndexesIfParentOrChildRelationship({
      groupNumber,
      indexes,
      questions,
      sections,
      sourceQuestionId,
      targetQuestion: question
    }));

    // If it's a group question (denoted by having a truthy 'groupId' property) and a 'groupNumber' is specified
    // try and find the particular group question
    const groupNumberValue = groupNumberOverride === undefined ? groupNumber : groupNumberOverride;
    const { groupId, questionId: qid } = question;
    if (groupNumberValue !== undefined && groupId) {
      // Check if it's a parent question, if so, get the index and find it within the child branch.
      question = getItemFromMap({
        questionId: qid,
        groupNumber: groupNumberValue,
        indexes,
        map: questions
      });
    }
  }

  return question;
}

function findBasicQuestion<QuestionT extends HpQuestionBasic>(
  questionId: string,
  questions: SMap<QuestionT>,
  notFoundAction: HpQuestionNotFoundAction
): QuestionT | undefined {
  // Ensure that the 'basic' question exists (without group numbers)
  let question = getItemFromMap({
    questionId,
    groupNumber: undefined,
    indexes: undefined,
    map: questions
  });

  if (!question) {
    // Try to find the question by name
    const qid = findQuestionId({
      name: questionId,
      groupNumber: undefined,
      questions,
      notFoundAction
    });

    if (qid) {
      question = getItemFromMap({
        questionId: qid,
        groupNumber: undefined,
        map: questions,
        indexes: undefined
      });
    }
  }

  return question;
}

export interface HpModifyIndexesIfParentOrChildRelationshipProps<
  QuestionT extends HpQuestionBasic,
  SectionT extends HpSectionBasic
> {
  groupNumber: number | undefined;
  indexes: number[] | undefined;
  questions: SMap<QuestionT>;
  sections: SMap<SectionT>;
  sourceQuestionId: string;
  targetQuestion: QuestionT;
}

export interface HpModifyIndexesIfParentOrChildRelationshipResult {
  groupNumber: number | undefined;
  indexes: number[] | undefined;
}

export function modifyIndexesIfParentOrChildRelationship<
  QuestionT extends HpQuestionBasic,
  SectionT extends HpSectionBasic
>({
  groupNumber,
  indexes,
  questions,
  sections,
  sourceQuestionId,
  targetQuestion
}: HpModifyIndexesIfParentOrChildRelationshipProps<
  QuestionT,
  SectionT
>): HpModifyIndexesIfParentOrChildRelationshipResult {
  if (!sourceQuestionId) {
    return { groupNumber, indexes };
  }

  const { groupId: targetGroupId } = targetQuestion;
  const { questionId: sourceId } = destructureControlId(sourceQuestionId, { requirePrefix: false });
  const sourceQuestion = ensureDefined(questions[sourceId], `questions[${sourceId}]`);
  const sourceGroupId = sourceQuestion ? sourceQuestion.groupId : undefined;
  const sourceSection = sourceGroupId ? sections[sourceGroupId] : undefined;
  const targetSection = targetGroupId ? sections[targetGroupId] : undefined;

  if (sourceGroupId !== targetGroupId && sourceSection && targetSection) {
    const distance = calculateDistanceBetweenSections(sourceSection, targetSection, sections);
    // IMPORTANT! Do not modify the indexes argument, instead copy to prevent side effects
    if (distance === 1) {
      if (groupNumber !== undefined) {
        if (!indexes) {
          indexes = [];
        }

        indexes = [...indexes, groupNumber];
        groupNumber = undefined;
      }
    } else if (distance < 0) {
      const len = indexes ? indexes.length : 0;
      if (indexes && len > 0) {
        groupNumber = indexes[len + distance];
        indexes = indexes.slice(0, len + distance);
        if (indexes.length === 0) {
          indexes = undefined;
        }
      }
    }
  }

  return { groupNumber, indexes };
}

/**
 * Calculates the distance between two sections where:
 *   0 ... the sections are the same
 *   1 ... the source is the parent of the target
 *  -1 ... the target is the parent of the source
 *  -2 ... the target is the grand-parent of the source
 *  etc.
 *
 * Note that the case where the target is a grand-child of the source is intentionally unhandled.
 * This is because it seems unnecessary and complicated for the question-set to handle such a scenario.
 * @param source the source section
 * @param target the target section
 * @param sections the sections
 * @returns a number indicating the distance between the sections
 */
export function calculateDistanceBetweenSections(
  source: HpSectionBasic,
  target: HpSectionBasic,
  sections: SMap<HpSectionBasic>
): number {
  let distance: number;
  const { parentId: sourceParentId, sectionId: sourceSectionId } = source;
  const { parentId: targetParentId, sectionId: targetSectionId } = target;
  if (sourceSectionId === targetSectionId) {
    distance = 0;
  } else if (!!targetParentId && targetParentId === sourceSectionId) {
    distance = 1;
  } else if (maxGroupNesting === 1) {
    return -1;
  } else {
    let id = sourceParentId;
    let d = 0;
    while (id !== undefined && id !== targetParentId) {
      const s = sections[id];
      if (s === undefined) {
        ensureDefined(s, `sections[${id}]`);
      }

      id = s.parentId;
      if (s.parentId === undefined && s.isRepeater) {
        throw new HpError(
          `When multiple nesting is used, all group sections must have 'parentId' populated. Invalid section: '${s.sectionId}'`
        );
      }

      d++;
    }

    distance = -d;
  }

  return distance;
}

interface GetAnswerFuncProps<QuestionT extends HpQuestionBasic, SectionT extends HpSectionBasic>
  extends GetQuestionFuncProps<QuestionT, SectionT> {
  answers: SMap<HpClientAnswer>;
}

function getAnswerFunc<QuestionT extends HpQuestionBasic, SectionT extends HpSectionBasic>({
  answers,
  groupNumber,
  indexes,
  options,
  props,
  questions,
  sections,
  sourceQuestionId,
  sourceQuestionType
}: GetAnswerFuncProps<QuestionT, SectionT>): HpClientAnswer | undefined {
  const question = getQuestionFunc({
    groupNumber,
    indexes,
    options,
    props,
    questions,
    sections,
    sourceQuestionId,
    sourceQuestionType
  });

  if (question) {
    const answer = answers[question.questionId];
    return answer || { customer: null, default: null };
  }

  return undefined;
}

function getAnswerValueFunc<
  QuestionT extends HpClientQuestion | HpQuestion,
  SectionT extends HpClientSection | HpSection
>({
  answers,
  groupNumber,
  indexes,
  options,
  props,
  questions,
  sections,
  sourceQuestionId,
  sourceQuestionType
}: GetAnswerFuncProps<QuestionT, SectionT>): HpAnswerValueTypes {
  const question = getQuestionFunc({
    groupNumber,
    indexes,
    options,
    props,
    questions,
    sections,
    sourceQuestionId,
    sourceQuestionType
  });

  let answerValue: HpAnswerValueTypes = null;

  if (question) {
    const { questionId } = question;
    let answer: HpClientAnswer | undefined;

    if (groupNumber !== undefined) {
      if (!indexes) {
        const questionAndGroupId =
          destructureControlId(questionId, { requirePrefix: false }).groupNumber === undefined
            ? createControlId({ groupNumber, questionId, options: { noPrefix: true } })
            : questionId;
        answer = answers[questionAndGroupId];
      } else {
        ({ indexes, groupNumber } = modifyIndexesIfParentOrChildRelationship({
          sourceQuestionId,
          targetQuestion: question,
          groupNumber,
          questions,
          sections,
          indexes
        }));

        const questionAndGroupId = createControlId({
          groupNumber,
          questionId,
          indexes,
          options: { noPrefix: true }
        });
        answer = answers[questionAndGroupId];
      }
    }

    if (!answer) {
      answer = answers[questionId];
    }

    if (answer) {
      ({ answerValue } = getAnswerValue(answer, question, 'prerequisites'));
    }
  }

  return answerValue;
}

interface GetItemFromMapProps<ResultT> {
  questionId: string;
  groupNumber: number | undefined;
  map: SMap<ResultT>;
  indexes: number[] | undefined;
}

function getItemFromMap<ResultT>({
  questionId,
  groupNumber,
  map,
  indexes
}: GetItemFromMapProps<ResultT>): ResultT | undefined {
  let qid = questionId;
  if (groupNumber !== undefined || (indexes && indexes.length > 0)) {
    qid = createControlId({
      questionId,
      groupNumber,
      indexes,
      options: { noPrefix: true, ignorePrefix: true }
    });
  }

  return map[qid];
}

function getIdFromName(name: string, groupNumber: number | undefined): string {
  ensureDefined(name, 'name');
  return findQuestionId({ name, questions: {}, groupNumber, onlyFromCache: true });
}

export function isElementVisible(
  questionId: string,
  groupNumber: number | undefined,
  displayState: HpQuestionDisplayState
): boolean {
  return !!getElement(questionId, groupNumber, displayState);
}

function getElement(
  questionId: string,
  groupNumber: number | undefined,
  displayState: HpQuestionDisplayState
): HTMLElement | undefined {
  ensureDefined(questionId, 'questionId');
  const id = getIdFromName(questionId, groupNumber);
  const controlId = createControlId({ questionId: id });
  if (id === '') {
    return undefined; // this might be the case on the server.
  }

  // let stateHasElement = false;
  // let browserHasElement = false;
  let html: HTMLElement | undefined;

  if (hasWindowRef) {
    const elements = jQuery('#' + controlId.replace(/~/g, '\\~'));
    if (elements.length === 1) {
      html = elements.get(0);
      // browserHasElement = true;
    } else {
      logInfo(
        elements.length === 0
          ? `There is no control with id="${controlId}"`
          : `There are ${elements.length} controls with id="${controlId}"; undefined will be returned`,
        HpClientDebugFlags.ValidatorExpressions
      );
    }
  } else {
    const isVisibleId =
      displayState.visibleQuestionIds.findIndex((el) => {
        if (typeof el === 'string') {
          return el === id;
        }

        return el.questions.indexOf(id) >= 0;
      }) >= 0;
    if (isVisibleId) {
      const qui = displayState.questionsUI[id];
      if (!qui || qui.isVisible) {
        const htmlTag: Partial<HTMLElement> = { tagName: 'Fake', id: controlId };
        html = htmlTag as HTMLElement;
        // stateHasElement = true;
      }
    }
  }

  // TODO: Ideally have the browser and state in perfect synchronsiation
  // if (browserHasElement !== stateHasElement) {
  //   throw new Error(
  //     `The browser and state disagree with regard to visibility of ${objectToErrorOutput({
  //       questionId,
  //       groupNumber
  //     })}: ${objectToErrorOutput({ browser: browserHasElement, state: stateHasElement })}`
  //   );
  // }

  return html;
}
